import { CalendarCheck, MessageCircle, ShieldCheck, Stethoscope, Sparkles, Clock } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const BADGES = [
  { icon: ShieldCheck, label: "Accurate Reports" },
  { icon: Stethoscope, label: "Experienced Radiologist" },
  { icon: Sparkles, label: "Modern Imaging" },
  { icon: Clock, label: "Timely Service" },
];

const HIGHLIGHTS = ["Ultrasound", "Digital X-Ray", "OPG Dental X-Ray", "Doppler Studies"];

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden bg-mesh-blue"
    >
      <img
        src="https://static.prod-images.emergentagent.com/jobs/3b2809c0-fffa-43af-989a-222e044bac64/images/fabcff5277bedba9c5fcfd582db091803ea44fe7dc40a091b198314449c51ded.png"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white/95 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in-up">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 border border-[#00BFFF]/30 text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase text-[#0099CC] shadow-sm"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00BFFF]" />
              Trusted Diagnostic Imaging Centre · Wagholi, Pune
            </span>

            <h1
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]"
              data-testid="hero-headline"
            >
              Advanced Diagnostic Imaging with{" "}
              <span className="text-[#0099CC]">Care, Accuracy &amp; Trust.</span>
            </h1>

            <p className="mt-5 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="hero-subheading">
              From everyday ultrasounds to detailed 3D/4D pregnancy scans, every study at
              Nidan is performed on modern equipment and read by an experienced radiologist —
              so you walk out with clarity, not questions.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#appointment"
                data-testid="hero-book-appointment-btn"
                className="inline-flex items-center gap-2 bg-[#00BFFF] hover:bg-[#0099CC] text-white font-semibold py-3.5 px-7 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <CalendarCheck className="w-5 h-5" />
                Book Appointment
              </a>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold py-3.5 px-7 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp Inquiry
              </a>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="hero-trust-badges">
              {BADGES.map((b) => (
                <div
                  key={b.label}
                  className="flex items-center gap-3 bg-white/70 backdrop-blur-sm border border-slate-100 rounded-2xl px-4 py-3 shadow-[0_4px_16px_rgba(15,23,42,0.04)]"
                >
                  <div className="shrink-0 w-9 h-9 rounded-xl bg-[#00BFFF]/10 text-[#0099CC] flex items-center justify-center">
                    <b.icon className="w-4.5 h-4.5" strokeWidth={2.2} />
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-slate-700 leading-tight">
                    {b.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative bg-white rounded-3xl border border-slate-100 shadow-[0_30px_60px_rgba(15,23,42,0.08)] p-6 sm:p-8 animate-fade-in-up">
              <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
                <div className="w-11 h-11 rounded-2xl bg-[#00BFFF]/10 flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-[#0099CC]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium">Today’s Hours</p>
                  <p className="text-base font-semibold text-slate-900">Mon – Sat · 10:00 AM to 9:00 PM</p>
                </div>
              </div>

              <ul className="mt-5 space-y-3">
                {HIGHLIGHTS.map((h) => (
                  <li
                    key={h}
                    data-testid={`hero-highlight-${h.replace(/\s+/g, "-").toLowerCase()}`}
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50"
                  >
                    <span className="text-sm font-semibold text-slate-800">{h}</span>
                    <span className="text-xs font-medium text-[#0099CC] bg-[#00BFFF]/10 px-2.5 py-1 rounded-full">
                      Available
                    </span>
                  </li>
                ))}
              </ul>

              <a
                href="#services"
                data-testid="hero-explore-services"
                className="mt-6 inline-flex w-full justify-center items-center gap-2 border-2 border-slate-200 hover:border-[#00BFFF] hover:text-[#0099CC] text-slate-800 font-semibold py-3 px-6 rounded-full transition-all"
              >
                Explore All Services
              </a>
            </div>

            <div className="hidden md:block absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#00BFFF]/15 blur-2xl" />
            <div className="hidden md:block absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-[#0099CC]/15 blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}
